import { ArrowDownRight, ArrowUpRight, type LucideIcon } from 'lucide-react';
import { formatNumber } from '@/utils/number';
import type { PcmpPerformanceDivision } from '../../types/pcmpReports.types';

interface InsightProps {
  title: string;
  division: PcmpPerformanceDivision | null;
  icon: LucideIcon;
}

function Insight({ title, division, icon: Icon }: InsightProps) {
  return (
    <div className="flex items-center gap-3 rounded-xl border bg-card px-4 py-3 shadow-sm">
      <span
        className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted"
        style={division ? { color: division.color } : undefined}
      >
        <Icon className="size-4" aria-hidden />
      </span>
      <div className="flex min-w-0 flex-col leading-tight">
        <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          {title}
        </span>
        {division ? (
          <>
            <span className="truncate font-semibold">{division.label}</span>
            <span className="text-sm tabular-nums text-muted-foreground">
              {division.subtotal.cmpPercent.toFixed(2)}% CMP ·{' '}
              {formatNumber(division.subtotal.instituteCount)} institutes
            </span>
          </>
        ) : (
          <span className="text-sm text-muted-foreground">No division has a sales target.</span>
        )}
      </div>
    </div>
  );
}

interface PcmpPerformanceInsightsProps {
  lowest: PcmpPerformanceDivision | null;
  highest: PcmpPerformanceDivision | null;
}

/** The divisions with the highest and the lowest CMP % (among those with a sales target). */
export function PcmpPerformanceInsights({ lowest, highest }: PcmpPerformanceInsightsProps) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <Insight title="Highest CMP %" division={highest} icon={ArrowUpRight} />
      <Insight title="Lowest CMP %" division={lowest} icon={ArrowDownRight} />
    </div>
  );
}
